/**
 * Общие части паспорта для карточки и формы: навигация по блокам, заголовок
 * блока, список «подпись — значение», таблица по зонам и годам. Карточка и
 * форма выглядят одинаково — меняется только содержимое ячеек.
 */
import styled from 'styled-components';
import { fleetLeading, fleetType } from './tokens';

/** Высота липкой навигации по блокам — на неё блоки отступают при переходе. */
const NAV_OFFSET = 14;

/**
 * Липкая навигация по блокам внутри `DrawerBody`. Отрицательные отступы
 * повторяют поля панели: полоса идёт от края до края, а `top` компенсирует
 * верхнее поле — иначе полоса липнет ниже края и под ней видно содержимое.
 */
export const BlockNav = styled.nav`
  position: sticky;
  top: -${({ theme }) => theme.spacing(5)};
  z-index: 2;
  margin: ${({ theme }) => `0 -${theme.spacing(6)} ${theme.spacing(2)}`};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(6)}`};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @container drawer (max-width: 520px) {
    top: -${({ theme }) => theme.spacing(4)};
    margin: ${({ theme }) => `0 -${theme.spacing(4)} ${theme.spacing(2)}`};
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  }
`;

export const BlockNavList = styled.ul`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin: 0;
  padding: 2px;
  list-style: none;
  overflow-x: auto;
`;

export const BlockNavButton = styled.button`
  display: inline-flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2.5)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  font-size: ${fleetType.small};
  white-space: nowrap;
  cursor: pointer;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  & > .n {
    font-family: ${({ theme }) => theme.font.mono};
    font-size: ${fleetType.micro};
    color: ${({ theme }) => theme.colors.text.muted};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &[data-alert='true'] {
    border-color: ${({ theme }) => theme.colors.status.danger};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const BlockSection = styled.section`
  padding-top: ${({ theme }) => theme.spacing(5)};
  scroll-margin-top: ${({ theme }) => theme.spacing(NAV_OFFSET)};

  & + & {
    margin-top: ${({ theme }) => theme.spacing(2)};
  }
`;

/** «01 Идентификация» — номер блока моноширинным, как графа бланка. */
export const BlockHeading = styled.h3`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2.5)};
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: ${fleetType.lead};
  font-weight: 600;
  line-height: ${fleetLeading.tight};
  text-wrap: balance;

  /* Фокус сюда ставит переход по навигации — это точка чтения, а не
     элемент управления (в Tab-обходе его нет), рамка не нужна. */
  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  & > .n {
    flex: none;
    font-family: ${({ theme }) => theme.font.mono};
    font-size: ${fleetType.small};
    font-weight: 400;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const BlockEmpty = styled.p`
  margin: 0;
  padding: ${({ theme }) => `${theme.spacing(2)} 0`};
  font-size: ${fleetType.body};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const BlockNote = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

export const KvList = styled.dl`
  margin: 0;
`;

/** Строка «подпись — значение»: две колонки, в узкой панели — одна под другой. */
export const KvRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => `${theme.spacing(2)} 0`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  & > dt {
    font-size: ${fleetType.body};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & > dd {
    margin: 0;
    font-size: ${fleetType.base};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.primary};
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }

  & > dd[data-multiline='true'] {
    white-space: pre-wrap;
  }

  @container drawer (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 2px;
  }
`;

/** Подзаголовок группы строк: «Нормы на 100 л топлива» над видами масел. */
export const KvGroupTitle = styled.p`
  margin: 0;
  padding: ${({ theme }) => `${theme.spacing(3)} 0 ${theme.spacing(1)}`};
  font-size: ${fleetType.body};
  font-weight: 600;
  line-height: ${fleetLeading.tight};
  color: ${({ theme }) => theme.colors.text.primary};
  text-wrap: pretty;
`;

/** Строки группы — подпись с отступом, колонки те же, что у `KvRow`. */
export const KvSubRow = styled(KvRow)`
  & > dt {
    padding-left: ${({ theme }) => theme.spacing(3)};
  }
`;

/** Таблица по зонам или годам: первая колонка — подпись строки, липкая. */
export const Matrix = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${fleetType.body};
  font-variant-numeric: tabular-nums;

  caption {
    padding-bottom: ${({ theme }) => theme.spacing(1.5)};
    text-align: left;
    font-size: ${fleetType.body};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  th,
  td {
    padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2.5)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  thead th {
    min-width: 64px;
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.04em;
    line-height: ${fleetLeading.tight};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-align: right;
    text-wrap: balance;
    vertical-align: bottom;
    background: ${({ theme }) => theme.colors.surface};
  }

  thead th:first-child,
  tbody th {
    position: sticky;
    left: 0;
    z-index: 1;
    text-align: left;
  }

  tbody th {
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.secondary};
    background: ${({ theme }) => theme.colors.backgroundElevated};
    min-width: 150px;
    text-wrap: pretty;
  }

  @container drawer (max-width: 520px) {
    tbody th {
      min-width: 112px;
    }
  }

  td {
    text-align: right;
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  td[data-empty='true'] {
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

export const MatrixBox = styled.div`
  padding: ${({ theme }) => `${theme.spacing(3)} 0 ${theme.spacing(1)}`};
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

/** Номер блока «01» — для навигации и заголовков. */
export function blockNumber(index: number): string {
  return String(index + 1).padStart(2, '0');
}

/** id якоря блока внутри панели. */
export function blockAnchor(prefix: string, blockId: string): string {
  return `${prefix}-${blockId}`;
}

/** Перейти к блоку: прокрутить к началу и поставить фокус на заголовок. */
export function jumpToBlock(prefix: string, blockId: string): void {
  const section = document.getElementById(blockAnchor(prefix, blockId));
  if (!section) return;
  // Без behavior: плавность берётся из CSS прокручиваемой панели и сама
  // гасится при prefers-reduced-motion.
  section.scrollIntoView({ block: 'start' });
  section.querySelector<HTMLElement>('h3')?.focus({ preventScroll: true });
}
